import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Tenant {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

export function TenantsPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api<{ tenants: Tenant[] }>('/api/v1/tenants'),
  });

  return (
    <Page
      title="Tenants"
      actions={
        <Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New tenant'}</Button>
      }
    >
      {showForm && (
        <div className="mb-4">
          <TenantForm onSaved={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['tenants'] });
          }} />
        </div>
      )}

      {isLoading ? (
        <Empty>Loading…</Empty>
      ) : !data?.tenants.length ? (
        <Empty>No tenants yet.</Empty>
      ) : (
        <Table>
          <thead className="text-left text-slate-500">
            <tr>
              <th className="border-b px-3 py-2 dark:border-slate-700">Name</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Phone</th>
              <th className="border-b px-3 py-2 dark:border-slate-700">Email</th>
            </tr>
          </thead>
          <tbody>
            {data.tenants.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="px-3 py-2">
                  <Link to={`/tenants/${t.id}`} className="font-medium underline decoration-dotted">
                    {t.fullName}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-500">{t.phone ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">{t.email ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

function TenantForm({ onSaved }: { onSaved: () => void }) {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ tenant: Tenant }>('/api/v1/tenants', { method: 'POST', body }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      fullName,
      phone: phone || undefined,
      email: email || undefined,
      address: address || undefined,
    });
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
        <Field label="Full name">
          <TextInput required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label="Phone">
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 …" />
        </Field>
        <Field label="Email">
          <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Address">
          <TextInput value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <div className="md:col-span-2 flex items-center justify-end gap-2">
          {error && <span className="text-sm text-red-600">{error}</span>}
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Create tenant'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
